import React, { useEffect, useCallback, useRef } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Dispatch, bindActionCreators } from 'redux';
import BarthelRecordForm from '../../components/Records/Barthel/BarthelRecordForm';
import * as RecordActions from '../../actions/Records/actions';
import * as UserActions from '../../actions/User/actions';
import { RootState } from '../../store/configureStore';
import Loading from '../../components/common/Loading';
import { BarthelRecord } from '../../constants/Records';

interface RouteParams {
  institutionId: string;
  userId: string;
  recordTime: string;
}

interface StateProps {
  institution: any;
  userListData: any[];
  barthel: any;
}

interface DispatchProps {
  recordActions: typeof RecordActions;
  userActions: typeof UserActions;
}

type Props = StateProps & DispatchProps;

const BarthelRecordEditContainer: React.FC<Props> = ({
  institution,
  userListData,
  barthel,
  recordActions,
  userActions,
}) => {
  const { institutionId, userId, recordTime } = useParams<RouteParams>();
  const userFetchedRef = useRef(false);
  const recordFetchedRef = useRef(false);

  const user = userListData.find((u: any) => u.user_id === userId);
  const uniqueId = user?.unique_id;

  useEffect(() => {
    if (userListData.length === 0 && !userFetchedRef.current && institutionId) {
      userFetchedRef.current = true;
      userActions.getUsersWithUniqueId(institutionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [institutionId, userListData.length]);

  useEffect(() => {
    if (uniqueId && recordTime && !recordFetchedRef.current) {
      recordFetchedRef.current = true;
      recordActions.fetchBarthelRecord(uniqueId, decodeURIComponent(recordTime));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uniqueId, recordTime]);

  const handleFetchRecord = useCallback(
    (id: string, time: string) => {
      recordActions.fetchBarthelRecord(id, time);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleCreate = useCallback(
    (id: string, data: Partial<BarthelRecord>) => {
      recordActions.createBarthelRecord(id, data);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleUpdate = useCallback(
    (id: string, time: string, data: Partial<BarthelRecord>) => {
      recordActions.updateBarthelRecord(id, time, data);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleResetFlags = useCallback(() => {
    recordActions.resetBarthelFlags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!user) {
    return <Loading />;
  }

  return (
    <BarthelRecordForm
      institutionId={institutionId}
      institutionName={institution?.institution_name || ''}
      userId={userId}
      userName={user.user_name}
      uniqueId={user.unique_id || ''}
      currentRecord={barthel.current}
      isEdit={true}
      executing={barthel.executing}
      created={barthel.created}
      updated={barthel.updated}
      onFetchRecord={handleFetchRecord}
      onCreate={handleCreate}
      onUpdate={handleUpdate}
      onResetFlags={handleResetFlags}
    />
  );
};

function mapStateToProps(state: RootState): StateProps {
  return {
    institution: state.institution.institution,
    userListData: state.user.listData,
    barthel: state.records.barthel,
  };
}

function mapDispatchToProps(dispatch: Dispatch): DispatchProps {
  return {
    recordActions: bindActionCreators(RecordActions, dispatch),
    userActions: bindActionCreators(UserActions, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(BarthelRecordEditContainer);
